import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { ArrowLeft } from 'lucide-react';
import { EntityPicker } from './EntityPicker';

/**
 * Dedicated Delivery Challan form (issue: "challan uses the Sales-Order
 * form"). A challan is created against a Sales Order / Invoice and tracks
 * dispatch details + delivery status. DeliveryChallan has no line table on
 * the backend (G73), so the dispatched items are kept in the header
 * `description` JSON and shown read-only.
 */

interface ChallanLine { key: string; productName: string; quantity: number; unit: string }
const rid = () => Math.random().toString(36).slice(2);
const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

const STATUSES = ['Pending', 'Dispatched', 'In Transit', 'Delivered', 'Cancelled'];

export default function DeliveryChallanComposer() {
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [challanNumber, setChallanNumber] = useState(isEdit ? '' : `DC-${Date.now().toString(36).toUpperCase().slice(-6)}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [customerId, setCustomerId] = useState<number | string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [relatedOrderId, setRelatedOrderId] = useState<number | string | null>(null);
  const [orderLabel, setOrderLabel] = useState('');
  const [warehouseId, setWarehouseId] = useState<number | string | null>(null);
  const [warehouseName, setWarehouseName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverName, setDriverName] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [eWayBillNumber, setEWayBillNumber] = useState('');
  const [challanStatus, setChallanStatus] = useState('Pending');
  const [lines, setLines] = useState<ChallanLine[]>([]);
  const [editRowId, setEditRowId] = useState<number | string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = pickList((await apiClient.get('/sales/DeliveryChallan')).data);
        const row = rows.find((x) => String(x.code ?? '') === String(id) || String(x.id ?? '') === String(id));
        if (!row || cancelled) return;
        setEditRowId((row.id ?? null) as number | string | null);
        setChallanNumber(String(row.challanNumber ?? row.code ?? id));
        if (row.challanDate) setDate(String(row.challanDate).slice(0, 10));
        if (row.customerId != null) setCustomerId(row.customerId as number);
        setCustomerName(String(row.customerName ?? ''));
        if (row.relatedOrderId != null) { setRelatedOrderId(row.relatedOrderId as number); setOrderLabel(`#${row.relatedOrderId}`); }
        if (row.warehouseId != null) { setWarehouseId(row.warehouseId as number); setWarehouseName(String(row.warehouseName ?? `#${row.warehouseId}`)); }
        setVehicleNumber(String(row.vehicleNumber ?? ''));
        setDriverName(String(row.driverName ?? ''));
        setTransporterName(String(row.transporterName ?? ''));
        setEWayBillNumber(String(row.eWayBillNumber ?? ''));
        if (row.challanStatus) setChallanStatus(String(row.challanStatus));
        try {
          const meta = JSON.parse(String(row.description ?? '{}')) as { lines?: Array<Record<string, unknown>> };
          if (Array.isArray(meta.lines)) setLines(meta.lines.map((l) => ({ key: rid(), productName: String(l.name ?? l.productName ?? ''), quantity: Number(l.quantity ?? 1) || 1, unit: String(l.unit ?? 'PCS') })));
        } catch { /* no envelope */ }
      } catch { /* blank */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, id]);

  async function pickOrder(so: Record<string, unknown> | null) {
    if (!so) { setRelatedOrderId(null); setOrderLabel(''); return; }
    const soId = (so.id ?? null) as number | null;
    setRelatedOrderId(soId);
    setOrderLabel(String(so.orderNumber ?? so.code ?? `#${soId}`));
    if (so.customerId != null) setCustomerId(so.customerId as number);
    if (so.customerName) setCustomerName(String(so.customerName));
    try {
      const lr = pickList((await apiClient.get('/sales/SalesOrderLine')).data)
        .filter((x) => String(x.salesOrderId) === String(soId))
        .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
      setLines(lr.map((l) => ({ key: rid(), productName: String(l.productName ?? l.name ?? ''), quantity: Number(l.quantity ?? 1) || 1, unit: String(l.unitOfMeasure ?? 'PCS') })));
      toast.success('Loaded', `Pulled ${lr.length} item(s) from ${String(so.orderNumber ?? so.code ?? '')}`);
    } catch { toast.error('Line copy failed', 'Loaded the order header only.'); }
  }

  const lineCount = useMemo(() => lines.filter((l) => l.productName.trim()).length, [lines]);

  async function save() {
    if (!customerId) { setErr('Select a customer or a reference order.'); return; }
    setSaving(true); setErr(null);
    try {
      const payload: Record<string, unknown> = {
        code: challanNumber, name: `Delivery Challan ${challanNumber}`, status: 0,
        challanNumber, challanDate: new Date(date).toISOString(),
        customerId: Number(customerId), customerName,
        relatedOrderId: relatedOrderId ? Number(relatedOrderId) : null,
        warehouseId: warehouseId ? Number(warehouseId) : null,
        warehouseName: warehouseName || undefined,
        vehicleNumber: vehicleNumber || undefined, driverName: driverName || undefined,
        transporterName: transporterName || undefined, eWayBillNumber: eWayBillNumber || undefined,
        challanStatus,
        description: JSON.stringify({ lines: lines.filter((l) => l.productName.trim()).map((l) => ({ name: l.productName, quantity: l.quantity, unit: l.unit })) }),
      };
      if (isEdit) {
        await apiClient.put('/sales/DeliveryChallan', { ...payload, ...(editRowId != null ? { id: Number(editRowId) } : {}), code: challanNumber });
      } else {
        await apiClient.post('/sales/DeliveryChallan', payload);
      }
      toast.success('Saved', `Delivery Challan ${challanNumber} ${isEdit ? 'updated' : 'created'}`);
      navigate('/panel/delivery-challans');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg); toast.error('Save failed', msg);
    } finally { setSaving(false); }
  }

  const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary';
  return (
    <div className="-m-6 space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/panel/delivery-challans')} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back"><ArrowLeft size={16} /></button>
        <h1 className="text-base font-semibold">{isEdit ? 'Edit' : 'New'} Delivery Challan</h1>
      </div>
      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Challan No"><input className={inputCls} value={challanNumber} onChange={(e) => setChallanNumber(e.target.value)} /></Field>
          <Field label="Date"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="From Sales Order">
            <EntityPicker<Record<string, unknown>>
              basePath="/sales/SalesOrder" idKey="id" labelKey="orderNumber" secondaryKey="customerName"
              value={relatedOrderId}
              selectedRow={orderLabel ? ({ id: relatedOrderId, orderNumber: orderLabel } as Record<string, unknown>) : null}
              onChange={(so) => void pickOrder(so)} placeholder="Select order" />
          </Field>
          <Field label="Customer">
            <EntityPicker<Record<string, unknown>>
              basePath="/crm/Customer" idKey="id" labelKey="name" secondaryKey="code"
              value={customerId}
              selectedRow={customerName ? ({ id: customerId, name: customerName } as Record<string, unknown>) : null}
              onChange={(c) => { setCustomerId((c?.id ?? null) as number | null); setCustomerName(String(c?.name ?? '')); }}
              placeholder="Select customer" />
          </Field>
          <Field label="Dispatch Warehouse">
            <EntityPicker<Record<string, unknown>>
              basePath="/master-data/Warehouse" idKey="id" labelKey="name" secondaryKey="code"
              value={warehouseId}
              selectedRow={warehouseName ? ({ id: warehouseId, name: warehouseName } as Record<string, unknown>) : null}
              onChange={(w) => { setWarehouseId((w?.id ?? null) as number | null); setWarehouseName(String(w?.name ?? '')); }}
              placeholder="Select warehouse" />
          </Field>
          <Field label="Status">
            <select className={inputCls} value={challanStatus} onChange={(e) => setChallanStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Vehicle No"><input className={inputCls} value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} /></Field>
          <Field label="Driver Name"><input className={inputCls} value={driverName} onChange={(e) => setDriverName(e.target.value)} /></Field>
          <Field label="Transporter"><input className={inputCls} value={transporterName} onChange={(e) => setTransporterName(e.target.value)} /></Field>
          <Field label="E-Way Bill No"><input className={inputCls} value={eWayBillNumber} onChange={(e) => setEWayBillNumber(e.target.value)} /></Field>
        </div>
      </Card>

      <Card className="p-0">
        <div className="border-b border-border px-4 py-2 text-sm font-medium">Dispatched items {lineCount ? `(${lineCount})` : ''}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-card-hover/40 text-left text-[11px] text-content-muted">
              <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Unit</th></tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-content-muted">Pick a Sales Order to pull its items.</td></tr>
              ) : lines.map((l, i) => (
                <tr key={l.key} className="border-t border-border">
                  <td className="px-3 py-1.5 text-content-muted">{i + 1}</td>
                  <td className="px-3 py-1.5">{l.productName || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{l.quantity}</td>
                  <td className="px-3 py-1.5">{l.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/panel/delivery-challans')} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Challan'}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-content-secondary">{label}</span>{children}</label>;
}
